import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, gbp } from "../../lib/apiClient";
import { downloadCsv } from "../../lib/floorAuditCsv";
import { BarChart3, Download, ChevronDown, ChevronRight, Plus, BadgeCheck } from "lucide-react";
import { toast } from "../../components/ui/sonner";

const TH = "px-3 py-2 text-left text-[11px] uppercase tracking-wider font-semibold text-slate-500 whitespace-nowrap";
const TD = "px-3 py-2 text-sm align-top";
const slug = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-");
const esc = (v) => { const s = v == null ? "" : String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { dateStyle: "short", timeStyle: "short" }) : "");

const Chips = ({ items }) => (
  <div className="flex flex-wrap gap-1">
    {items.map((t) => <span key={t} className="px-1.5 py-0.5 rounded bg-slate-100 text-[11px] text-slate-700 whitespace-nowrap">{t}</span>)}
  </div>
);

const ACT = "inline-flex items-center gap-1 px-2 py-1 rounded border text-[11px] font-semibold uppercase tracking-wider whitespace-nowrap transition-colors";

const prefillFrom = (r) => ({
  name: r.name,
  types: r.types_used?.length ? r.types_used : ["materials"],
  categories: r.categories.map((c) => c.category).filter((c) => c !== "No category"),
});

const UsageTable = ({ rows, testid, empty, onAdd }) => (
  <div className="overflow-x-auto border border-slate-200 rounded-lg bg-white">
    <table className="min-w-full" data-testid={testid}>
      <thead className="bg-slate-50"><tr>
        {["Supplier", "Quotes", "Materials £", "Access Hire £", "Total £", "Estimators", "By category", ...(onAdd ? [""] : [])].map((h, i) => <th key={h || i} className={TH}>{h}</th>)}
      </tr></thead>
      <tbody>
        {rows.length === 0 && <tr><td colSpan={8} className="px-3 py-5 text-center text-sm text-slate-400">{empty}</td></tr>}
        {rows.map((r) => (
          <tr key={r.id || r.name} className={`border-t border-slate-100 ${r.quotes ? "" : "opacity-50"}`} data-testid={`${testid}-row-${slug(r.name)}`}>
            <td className={`${TD} font-semibold whitespace-nowrap`}>{r.name}</td>
            <td className={TD}>{r.quotes}</td>
            <td className={`${TD} font-mono`}>{gbp(r.materials_spend)}</td>
            <td className={`${TD} font-mono`}>{gbp(r.access_spend)}</td>
            <td className={`${TD} font-mono font-bold`}>{gbp(r.spend)}</td>
            <td className={TD}><Chips items={r.estimators.map((e) => `${e.name} (${e.quotes})`)} /></td>
            <td className={TD}><Chips items={r.categories.map((c) => `${c.category}: ${c.quotes} · ${gbp(c.spend)}`)} /></td>
            {onAdd && (
              <td className={TD}>
                <button onClick={() => onAdd(prefillFrom(r))} data-testid={`${testid}-add-${slug(r.name)}`} className={`${ACT} border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100`}>
                  <Plus size={12} /> Add to list
                </button>
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const approvedFor = (s, c) => !!s && (s.all_categories || (s.categories || []).includes(c));

const UnapprovedRow = ({ u, onAdd, onApprove, suppliers }) => {
  const sup = (suppliers || []).find((s) => s.id === u.supplier_id);
  const cats = [...new Set(u.confirmations.map((c) => c.category).filter((c) => c && c !== "No category"))];
  const stop = (fn) => (e) => { e.stopPropagation(); fn(); };
  const [open, setOpen] = useState(false);
  return (
    <>
      <tr className="border-t border-slate-100 cursor-pointer hover:bg-slate-50" onClick={() => setOpen(!open)} data-testid={`unapproved-usage-row-${slug(u.supplier)}`}>
        <td className={`${TD} font-semibold whitespace-nowrap`}>{open ? <ChevronDown size={14} className="inline mr-1" /> : <ChevronRight size={14} className="inline mr-1" />}{u.supplier}</td>
        <td className={`${TD} font-bold text-red-700`}>{u.count}</td>
        <td className={TD}>{u.on_list ? "Yes (not approved for that category)" : "No"}</td>
        <td className={TD}><Chips items={u.confirmed_by.map((c) => `${c.name} (${c.count})`)} /></td>
        <td className={TD}>
          <div className="flex flex-wrap gap-1">
            {u.on_list
              ? cats.map((c) => approvedFor(sup, c) ? (
                <span key={c} data-testid={`unapproved-approved-${slug(u.supplier)}-${slug(c)}`} className={`${ACT} border-slate-200 bg-white text-emerald-700`}>
                  <BadgeCheck size={12} /> Approved for {c}
                </span>
              ) : (
                <button key={c} onClick={stop(() => onApprove(u.supplier_id, c))} data-testid={`unapproved-approve-${slug(u.supplier)}-${slug(c)}`}
                  className={`${ACT} border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100`}>
                  <BadgeCheck size={12} /> Approve for {c}
                </button>
              ))
              : (
                <button onClick={stop(() => onAdd({ name: u.supplier, types: [...new Set(u.confirmations.map((c) => (c.section === "Access Hire" ? "access_hire" : "materials")))], categories: cats }))}
                  data-testid={`unapproved-add-${slug(u.supplier)}`} className={`${ACT} border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100`}>
                  <Plus size={12} /> Add & approve
                </button>
              )}
          </div>
        </td>
      </tr>
      {open && u.confirmations.map((c, i) => (
        <tr key={i} className="bg-red-50/40 text-xs">
          <td className="px-3 py-1.5 pl-9"><Link to={`/quotes/${c.quote_id}`} className="text-blue-700 hover:underline font-semibold" data-testid={`unapproved-usage-link-${c.quote_id}`}>{c.quote_number || "Open quote"}</Link> <span className="text-slate-500">{c.customer}</span></td>
          <td className="px-3 py-1.5">{c.section}</td>
          <td className="px-3 py-1.5">{c.category}</td>
          <td className="px-3 py-1.5" colSpan={2}>{c.confirmed_by} · {fmtDate(c.confirmed_at)}</td>
        </tr>
      ))}
    </>
  );
};

const UnapprovedTable = ({ rows, onAdd, onApprove, suppliers }) => (
  <div className="overflow-x-auto border border-slate-200 rounded-lg bg-white">
    <table className="min-w-full" data-testid="unapproved-usage-table">
      <thead className="bg-slate-50"><tr>{["Supplier", "Times confirmed", "On your list?", "Confirmed by", ""].map((h, i) => <th key={h || i} className={TH}>{h}</th>)}</tr></thead>
      <tbody>
        {rows.length === 0 && <tr><td colSpan={5} className="px-3 py-5 text-center text-sm text-slate-400">No unapproved suppliers were confirmed in this period.</td></tr>}
        {rows.map((u) => <UnapprovedRow key={u.supplier} u={u} onAdd={onAdd} onApprove={onApprove} suppliers={suppliers} />)}
      </tbody>
    </table>
  </div>
);

function toCsv(d) {
  const out = [];
  const row = (...c) => out.push(c.map(esc).join(","));
  row(`Supplier Usage ${d.start} to ${d.end} (latest version of each quote; cost ex VAT)`);
  const usage = (title, rows) => {
    row(); row(title);
    row("Supplier", "Quotes", "Materials £", "Access Hire £", "Total £", "Estimators", "By Category");
    rows.forEach((r) => row(r.name, r.quotes, r.materials_spend.toFixed(2), r.access_spend.toFixed(2), r.spend.toFixed(2),
      r.estimators.map((e) => `${e.name} (${e.quotes})`).join("; "), r.categories.map((c) => `${c.category}: ${c.quotes} / ${c.spend.toFixed(2)}`).join("; ")));
  };
  usage("Suppliers on your list", d.listed);
  usage("Suppliers not on your list", d.unlisted);
  row(); row("Unapproved suppliers confirmed");
  row("Supplier", "Times Confirmed", "On List", "Quote No", "Customer", "Section", "Category", "Confirmed By", "Confirmed At");
  d.unapproved.forEach((u) => u.confirmations.forEach((c) => row(u.supplier, u.count, u.on_list ? "Yes" : "No", c.quote_number, c.customer, c.section, c.category, c.confirmed_by, fmtDate(c.confirmed_at))));
  return "\uFEFF" + out.join("\r\n") + "\r\n";
}

const H = ({ children }) => <h3 className="text-xs uppercase tracking-[0.2em] font-bold text-slate-700">{children}</h3>;

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const now = new Date();

export function SupplierUsageReport({ refreshKey, onAdd, onApprove, suppliers }) {
  const [start, setStart] = useState(ymd(new Date(now.getFullYear(), now.getMonth(), 1)));
  const [end, setEnd] = useState(ymd(new Date(now.getFullYear(), now.getMonth() + 1, 0)));
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);

  const ranRef = useRef(false);
  const run = async () => {
    ranRef.current = true;
    setBusy(true);
    try { setData((await api.get("/reports/supplier-usage", { params: { start, end } })).data); }
    catch { toast.error("Could not run the supplier usage report."); }
    finally { setBusy(false); }
  };

  useEffect(() => {
    if (refreshKey && ranRef.current) run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const date = (label, v, set, tid) => (
    <label className="text-xs uppercase tracking-wider font-semibold text-slate-600">{label}
      <input type="date" value={v} onChange={(e) => { set(e.target.value); setData(null); }} data-testid={tid} className="block mt-1 h-10 border border-slate-300 rounded px-2 text-sm" />
    </label>
  );

  return (
    <div className="qb-card p-4 space-y-5" data-testid="supplier-usage-report">
      <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-800">Supplier usage report</div>
      <div className="flex items-end gap-3 flex-wrap">
        {date("From", start, setStart, "supplier-usage-start")}
        {date("To", end, setEnd, "supplier-usage-end")}
        <button onClick={run} disabled={busy} data-testid="supplier-usage-run-btn" className="h-10 px-4 rounded-full bg-slate-900 text-white text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-2 disabled:opacity-50">
          <BarChart3 size={14} />{busy ? "Running…" : "Run report"}
        </button>
        <button onClick={() => downloadCsv(toCsv(data), `supplier-usage-${start}-to-${end}.csv`)} disabled={!data} data-testid="supplier-usage-download-btn"
          className="h-10 px-4 rounded-full border border-slate-300 text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-2 disabled:opacity-40">
          <Download size={14} />Download CSV
        </button>
      </div>
      {data && (
        <div className="space-y-5" data-testid="supplier-usage-results">
          <div className="text-sm text-slate-600" data-testid="supplier-usage-quote-count">{data.quotes_in_period} {data.quotes_in_period === 1 ? "quote" : "quotes"} in period (latest version of each). Cost is supplier cost ex VAT, before mark-up.</div>
          <div className="space-y-2"><H>Unapproved suppliers confirmed</H><UnapprovedTable rows={data.unapproved} onAdd={onAdd} onApprove={onApprove} suppliers={suppliers} /></div>
          <div className="space-y-2"><H>Suppliers on your list</H><UsageTable rows={data.listed} testid="supplier-usage-listed" empty="No suppliers on your list yet." /></div>
          <div className="space-y-2">
            <H>Not on your list</H>
            <p className="text-xs text-slate-500">Supplier names from quote lines that don't match any supplier you've added. "Add to list" opens the supplier form pre-filled with the name, type and categories it was used for.</p>
            <UsageTable rows={data.unlisted} testid="supplier-usage-unlisted" empty="Every supplier used is on your list." onAdd={onAdd} />
          </div>
        </div>
      )}
    </div>
  );
}
